import { App, AppUrls } from "../../ts/app";
import { Controller } from "../../ts/controller";
import { Logger } from "../../ts/utils/logger";

export class AppbarDropdownController extends Controller {
    dropdownVisible: boolean = false;
    loggingOut: boolean = false;
    currentTheme: string | null = null;

    constructor() {
        super({
            logLabel: "AppbarDropdownController",
        });
    }

    initialize(): void {
        this.currentTheme = window.themeController.currentTheme;

        this.bindClick({
            elementId: "appbar-dropdown-toggle-button",
            handler: (event: any) => {
                this.dropdownVisible = !this.dropdownVisible;
                this._setState();
                event.stopPropagation();
            },
        });

        if (App.loggedIn) {
            this.bindClick({
                elementId: "appbar-dropdown-logout-button",
                handler: this.logout.bind(this),
            });
        } else {
            this.bindClick({
                elementId: "appbar-dropdown-login-button",
                handler: () => {
                    window.loginDialogController.open(null);
                    this.dropdownVisible = false;
                    this._setState();
                },
            });
        }

        this.bindClick({
            elementId: "appbar-dropdown-toggle-theme-button",
            handler: () => {
                this.currentTheme =
                    this.currentTheme === "light" ? "dark" : "light";
                window.themeController.toggleTheme();
                this._setState();
            },
        });

        document.addEventListener("click", this.closeDropdown.bind(this));
        document.addEventListener("themeChange", () => {
            this.currentTheme = window.themeController.currentTheme;
            this._setState();
        });

        this._setState();
        Logger.info(this.logLabel, "Initialized");
    }

    private _setState(): void {
        this._setDropdownState();
        if (App.loggedIn) {
            this._setLogoutState();
        }
        this._setThemeState();
    }

    private _setDropdownState(): void {
        const dropdown = this.getElementByIdOrWarn("appbar-dropdown-menu");
        if (!dropdown) {
            return;
        }
        dropdown.classList.toggle("show", this.dropdownVisible);
    }

    private _setLogoutState(): void {
        const logoutButton = this.getElementByIdOrWarn(
            "appbar-dropdown-logout-button"
        );
        const loadingContainer = this.getElementByIdOrWarn(
            "appbar-dropdown-logout-button-loading-container"
        );

        if (logoutButton) {
            logoutButton.style.display = this.loggingOut ? "none" : "block";
        }
        if (loadingContainer) {
            loadingContainer.style.display = this.loggingOut ? "flex" : "none";
        }
    }

    private _setThemeState(): void {
        const elementId =
            this.currentTheme === "light"
                ? "icon-template-light-mode"
                : "icon-template-dark-mode";
        const iconTemplate = this.getElementByIdOrWarn(elementId);
        const toggleThemeButton = this.getElementByIdOrWarn(
            "appbar-dropdown-toggle-theme-button"
        );

        if (iconTemplate && toggleThemeButton) {
            toggleThemeButton.innerHTML =
                iconTemplate.innerHTML + " Theme";
        }
    }

    async logout(): Promise<void> {
        this.loggingOut = true;
        this._setState();
        try {
            const response = await fetch(AppUrls.logout, {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ currentUrl: window.location.pathname }),
            });

            if (!response.ok) {
                throw new Error("Logout failed");
            }
            const data = await response.json();
            if (data.shouldNavigateHome) {
                window.location.href = "/";
            } else {
                location.reload();
            }
        } catch (error) {
            Logger.error(this.logLabel, `Logout failed: ${error}`);
            this.loggingOut = false; // Only mark false on error
        } finally {
            this._setState();
        }
    }

    closeDropdown(event: MouseEvent): void {
        const dropdown = this.getElementByIdOrWarn("appbar-dropdown-menu");
        const toggleButton = this.getElementByIdOrWarn(
            "appbar-dropdown-toggle-button"
        );
        if (
            dropdown &&
            !dropdown.contains(event.target as Node) &&
            event.target !== toggleButton
        ) {
            this.dropdownVisible = false;
            this._setState();
        }
    }
}
